import type { AssetTypeEnum, ProxyTypeEnum } from 'venn-api';

// ============================================================================
// PROXY TYPE VALUE DEFINITIONS
// ProxyTypeValue is the source of truth. All other types are derived from it.
// Expanded union enables Extract/Exclude to work correctly.
// ============================================================================

export type ProxyTypeValue =
  | { proxyType: 'BACKFILL'; extrapolate: boolean }
  | { proxyType: 'DESMOOTH'; extrapolate: boolean }
  | { proxyType: 'DESMOOTH_INTERPOLATE'; extrapolate: boolean }
  | { proxyType: 'INTERPOLATE'; extrapolate: boolean }
  | { proxyType: 'SUBSTITUTE' }
  | { proxyType: 'EXTRAPOLATE' };

export type FirstLayerProxyTypeValue = ProxyTypeValue;

type ExtrapolatableProxyType = Extract<ProxyTypeValue, { extrapolate: boolean }>['proxyType'];

export type SecondLayerProxyTypeValue = Exclude<
  ProxyTypeValue,
  { proxyType: 'BACKFILL' } | { proxyType: 'SUBSTITUTE' }
>;

export type SecondLayerProxyType = SecondLayerProxyTypeValue['proxyType'];
type SecondLayerExtrapolatableType = Extract<SecondLayerProxyTypeValue, { extrapolate: boolean }>['proxyType'];
type SecondLayerNonExtrapolatableType = Exclude<SecondLayerProxyTypeValue, { extrapolate: boolean }>['proxyType'];

// ============================================================================
// TYPE GUARD ARRAYS (private, used by type guards)
// ============================================================================

const EXTRAPOLATABLE_PROXY_TYPES: ExtrapolatableProxyType[] = [
  'BACKFILL',
  'DESMOOTH',
  'DESMOOTH_INTERPOLATE',
  'INTERPOLATE',
];

const SECOND_LAYER_EXTRAPOLATABLE_TYPES: SecondLayerExtrapolatableType[] = [
  'DESMOOTH',
  'DESMOOTH_INTERPOLATE',
  'INTERPOLATE',
];

// ============================================================================
// TYPE GUARDS
// ============================================================================

export const isExtrapolatableProxyType = (proxyType: ProxyTypeEnum): proxyType is ExtrapolatableProxyType =>
  EXTRAPOLATABLE_PROXY_TYPES.includes(proxyType as ExtrapolatableProxyType);

const isSecondLayerExtrapolatableType = (proxyType: ProxyTypeEnum): proxyType is SecondLayerExtrapolatableType =>
  SECOND_LAYER_EXTRAPOLATABLE_TYPES.includes(proxyType as SecondLayerExtrapolatableType);

export const isSecondLayerProxyType = (proxyType: ProxyTypeEnum): proxyType is SecondLayerProxyType =>
  isSecondLayerExtrapolatableType(proxyType) || proxyType === 'EXTRAPOLATE';

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

export const createFirstLayerProxyTypeValue = (
  proxyType: ProxyTypeEnum,
  extrapolate: boolean,
): FirstLayerProxyTypeValue => {
  if (isExtrapolatableProxyType(proxyType)) {
    return { proxyType, extrapolate };
  }
  return { proxyType };
};

export const createSecondLayerProxyTypeValue = (
  proxyType: SecondLayerProxyType,
  extrapolate: boolean,
): SecondLayerProxyTypeValue => {
  if (isSecondLayerExtrapolatableType(proxyType)) {
    return { proxyType, extrapolate };
  }
  return { proxyType: proxyType as SecondLayerNonExtrapolatableType };
};

export const getExtrapolateFromValue = (value: FirstLayerProxyTypeValue | SecondLayerProxyTypeValue): boolean =>
  'extrapolate' in value ? value.extrapolate : false;

// ============================================================================

export interface DataPoint {
  /**
   * Timestamp, in UTC
   */
  x: number;
  /**
   * Value
   */
  y: number;
}

export interface DoubleDataPoint {
  /**
   * Timestamp, in UTC
   */
  x: number;
  /**
   * Value #1
   */
  y1: number;
  /**
   * Value #2
   */
  y2: number;
}

export interface Range {
  start: number;
  end: number;
}

export type Returns = DataPoint[];

export enum QuickFilter {
  RECENTLY_ANALYZED = 'recentlyAnalyzed',
  UPLOADED_BY_ANYONE = 'uploadedByAnyone',
  UPLOADED_BY_ME = 'uploadedByMe',
  INTEGRATIONS = 'integrations',
  REQUIRES_ATTENTION = 'requiresAttention',
}

export enum LibraryItemType {
  ALL = 'all',
  /* Portfolio */
  PORTFOLIO = 'portfolio',
  PORTFOLIO_INTEGRATION = 'portfolioIntegration',
  PRO_FORMA = 'proForma',
  HISTORICAL = 'historical',
  /* --------- */
  COMPOSITE_BENCHMARK = 'compositeBenchmark',
  /* Investment */
  INVESTMENT = 'investment',
  UPLOAD = 'upload',
  INVESTMENT_INTEGRATION = 'investmentIntegration',
  MASTER_PORTFOLIO_HOLDING = 'masterPortfolioHolding',
  VENN_PROVIDED = 'vennProvided',
  /* --------- */
  NONE = 'none',

  TAGS = 'tags',
}

/**
 * Represents the selected filters on the library side menu
 */
export interface SelectedFilters {
  assetTypes: AssetTypeEnum[];
  quickFilters: QuickFilter[];
  itemType: LibraryItemType;
  tags: string[];
}

/**
 * Uniquely identify each library side menu filter section and used as URL query param keys
 */
export enum LibraryFilterSectionKey {
  ASSET_TYPES = 'assetTypes',
  FILTERS = 'filters',
  ITEM_TYPE = 'itemType',
  TAGS = 'tags',
}

/**
 * The type of the filter in the advance search
 */
export enum FilterType {
  SOURCE = 'sourceFilter',
  ITEM_TYPE = 'itemTypeFilter',
  CURRENCY = 'currencyFilter',
  ASSET_TYPE = 'assetTypeFilter',
  DATA_SOURCE = 'dataSourceFilter',
  MORNINGSTAR_CATEGORY = 'morningstarCategoryFilter',
  TAX_STATUS = 'taxStatusFilter',
  ADVANCED_QUERY = 'advancedQuery',
}

export enum SavedViewsQuickFilter {
  RECENTLY_ANALYZED = 'RECENTLY_ANALYZED',
  REQUIRES_ATTENTION = 'REQUIRES_ATTENTION',
  SCHEDULED = 'SCHEDULED',
  SAVED_BY_ME = 'SAVED_BY_ME',
  RISKPRO_PROVIDED = 'riskpro_provided',
}

export enum RowDisplayLimitType {
  ALL = 'ALL',
  TOP_5 = 'TOP_5',
  TOP_10 = 'TOP_10',
}

export type ProxyTypeDetails = {
  value: ProxyTypeValue;
  label: string;
  description: string;
  disabled?: boolean;
};
